import './trades.scss'

import { Button, InputNumber, notification, Popover, Table } from 'antd'
import classnames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import { Column, Row } from 'react-display-flex'
import useSWR from 'swr'

import { fetchByDomain } from '../../../api'
import { SelectTenant } from '../../../components'
import { buildTableFilterKeys } from '../../../components/tables/build-table-filter-keys'
import { momentDateHandler } from '../../../date/moment-date-handler'
import { httpClient } from '../../../util/http-client'
import { CreateManagedFundDrawer } from '../CreateManagedFund/CreateManagedFundDrawer'
import { DeleteTradeModal } from '../DeleteTrade'
import { messages } from '../messages'
import { convertTradeToTableItems, Trade, TradeTableItem } from '../models'
import { UpdateCouponDatesDrawer } from '../UpdateCouponDates'
import { UpdateTradeDrawer } from '../UpdateTrade'
import { UploadTradesErrors, UploadTradesModal } from '../UploadTrades'
import { buildTradesTableColumns } from './trades-table-columns'
import { TradesSummary } from './TradesSummary'

const { formatIso, now } = momentDateHandler

export const Trades = () => {
  const [currentTenant, setCurrentTenant] = useState(null)
  const [tradeTableItems, setTradeTableItems] = useState([])
  const [loadTradeId, setLoadTradeId] = useState(null)

  const [tradeToUpdate, setTradeToUpdate] = useState(null)
  const [updateCouponDatesTradeId, setUpdateUpdateCouponDatesTradeId] = useState(null)
  const [tradeToDelete, setTradeToDelete] = useState(null)

  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false)
  const [uploadTradesFailed, setUploadTradesFailed] = useState([])

  const [isCreateManagedFundDrawerOpen, setIsCreateManagedFundDrawerOpen] = useState(false)

  const [filters, setFilters] = useState({
    instrumentCodes: [],
    issuerCodes: [],
    allocationCodes: [],
  })

  const customRef = useRef<HTMLButtonElement>(null)

  const { data, mutate } = useSWR(
    currentTenant ? [`/trades?query[asOf]=${formatIso({ date: now() })}`, currentTenant] : null,
    fetchByDomain,
  )

  useEffect(() => {
    if (!currentTenant || !data) return

    const tableItems = convertTradeToTableItems(data)

    setFilters({
      instrumentCodes: buildTableFilterKeys(tableItems, 'instrumentCode'),
      issuerCodes: buildTableFilterKeys(tableItems, 'issuerCode'),
      allocationCodes: buildTableFilterKeys(tableItems, 'allocationCode'),
    })

    setTradeTableItems(tableItems)
  }, [data, currentTenant])

  const onUpdateTrade = (trade: TradeTableItem) => setTradeToUpdate(trade)

  const onUpdateCouponDates = (tradeId: number) => setUpdateUpdateCouponDatesTradeId(tradeId)

  const onDeleteTrade = (tradeToDelete: TradeTableItem) => setTradeToDelete(tradeToDelete)

  const onSuccess = async () => {
    await mutate()
    onCloseDialogs()
  }

  const handleUpload = async ({ holdings }) => {
    const failedTradesOnCreate = []

    for (let trade of holdings) {
      trade.allocationCode = String(trade.allocationCode || 'general').toLocaleUpperCase()
      trade.countryCode = 'AU'

      try {
        await httpClient('/trades/term-deposits', { method: 'post', domain: currentTenant, data: trade })
      } catch (error) {
        failedTradesOnCreate.push({ trade, error: error.error })
      }
    }

    setUploadTradesFailed(failedTradesOnCreate)

    if (failedTradesOnCreate.length) {
      notification.error({ message: messages.common.error, description: messages.trade.create.error })
      return
    }

    await mutate()
    notification.success({ message: messages.common.success, description: messages.trade.create.success })
    setIsUploadModalVisible(false)
  }

  const columns = buildTradesTableColumns({ onUpdateTrade, onDeleteTrade, onUpdateCouponDates, filters })

  const onCloseDialogs = () => {
    setTradeToUpdate(null)
    setUpdateUpdateCouponDatesTradeId(null)
    setTradeToDelete(null)
    setUploadTradesFailed([])
    setLoadTradeId(null)
    setIsCreateManagedFundDrawerOpen(false)
  }

  const loadTradeById = () => {
    if (!loadTradeId) return

    setTradeToUpdate(loadTradeId)
    setLoadTradeId(null)
    customRef.current.click()
  }

  return (
    <Column className="trades">
      <SelectTenant onTenantSelected={setCurrentTenant} />

      <TradesSummary trades={tradeTableItems} isLoading={!!currentTenant && !data} />

      <Row className="action-buttons" justifyContentEnd>
        <Popover
          title="Load trade by ID"
          trigger="click"
          content={
            <Row justifyContentSpaceBetween>
              <InputNumber
                className="trade-id-wrapper"
                size="small"
                value={loadTradeId}
                min={1}
                autoFocus={true}
                onPressEnter={loadTradeById}
                onChange={(value) => setLoadTradeId(value)}
              />
              <Button size="small" type="primary" className="load-trade-id-button" onClick={loadTradeById}>
                Load
              </Button>
            </Row>
          }
        >
          <Button disabled={!currentTenant} ref={customRef}>
            Load trade by ID
          </Button>
        </Popover>

        <Button disabled={!currentTenant} onClick={() => setIsUploadModalVisible(true)} type="primary">
          Upload Trades
        </Button>

        <Button disabled={!currentTenant} onClick={() => setIsCreateManagedFundDrawerOpen(true)} type="primary">
          Create Managed Fund
        </Button>
      </Row>

      <Table
        rowClassName={(trade: Trade) =>
          classnames({ active: [tradeToUpdate, updateCouponDatesTradeId].includes(trade.id) })
        }
        dataSource={tradeTableItems}
        loading={!!currentTenant && !data}
        columns={columns}
        pagination={false}
        rowKey="id"
        size="small"
      />

      <UpdateTradeDrawer
        isVisible={tradeToUpdate}
        tradeId={tradeToUpdate?.id}
        instrumentCode={tradeToUpdate?.instrumentCode}
        domain={currentTenant}
        onSuccess={onSuccess}
        onClose={onCloseDialogs}
      />

      <DeleteTradeModal
        isVisible={tradeToDelete}
        trade={tradeToDelete}
        domain={currentTenant}
        onSuccess={onSuccess}
        onClose={onCloseDialogs}
      />

      <UpdateCouponDatesDrawer
        isVisible={updateCouponDatesTradeId}
        tradeId={updateCouponDatesTradeId}
        domain={currentTenant}
        onSuccess={onSuccess}
        onClose={onCloseDialogs}
      />

      <UploadTradesModal
        onUploadClick={handleUpload}
        onCancel={() => setIsUploadModalVisible(false)}
        visible={isUploadModalVisible}
      />

      <UploadTradesErrors
        failedTrades={uploadTradesFailed}
        isVisible={!!uploadTradesFailed.length}
        onClose={onCloseDialogs}
      />

      <CreateManagedFundDrawer
        isVisible={isCreateManagedFundDrawerOpen}
        domain={currentTenant}
        onSuccess={onSuccess}
        onClose={onCloseDialogs}
      />
    </Column>
  )
}
